/**
 * The top of every screen: what this page is, and the one action it offers.
 *
 * Using it everywhere is what makes six developers' screens feel like one
 * application (Plan Section 8, risk 2).
 */

import type { ReactNode } from 'react';
import { Breadcrumbs } from './Breadcrumbs';
import type { Crumb } from './Breadcrumbs';
import styles from './PageHeader.module.css';

export interface PageHeaderProps {
  title: string;
  /** One sentence. If it needs two, the screen is doing too much. */
  description?: string;
  actions?: ReactNode;
  /**
   * The trail below the dashboard, which `Breadcrumbs` prepends.
   *
   * Breadcrumbs live here rather than in the layout because only the screen knows
   * its own trail — a volunteer's name is not derivable from `/volunteers/:id`
   * without the record. Every screen already renders a `PageHeader`, so this puts
   * them in the same place on all of them for free.
   *
   * Omitted on the dashboard: it is the root, and a one-item trail repeating the
   * heading below it says nothing.
   */
  breadcrumbs?: Crumb[];
}

export function PageHeader({ title, description, actions, breadcrumbs }: PageHeaderProps) {
  return (
    <div className={styles.wrapper}>
      {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}

      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{title}</h1>
          {description && <p className={styles.description}>{description}</p>}
        </div>
        {actions && <div className={styles.actions}>{actions}</div>}
      </header>
    </div>
  );
}
